import * as XLSX from "xlsx";
import * as $ from "jquery";

interface PromptUserOptions {
  suggestedValues?: string[];
  possibleValues?: string[];
  requiredInput?: boolean;
  type?: "KeyColumns" | "ExcludedColumns";
}

export class Helper {
  private state: any;

  constructor(state: any) {
    this.state = state;
  }

  // Convert Excel column letter to array index (0-based)
  public colLetterToIndex(colLetter) {
    colLetter = colLetter.toUpperCase();
    let sum = 0;
    for (let i = 0; i < colLetter.length; i++) {
      sum = sum * 26;
      sum = sum + (colLetter.charCodeAt(i) - "A".charCodeAt(0) + 1);
    }
    return sum - 1; // Convert to 0-based index
  }

  // Convert array index to Excel column letter
  public indexToColLetter(index) {
    let temp = index + 1;
    let letter = "";

    while (temp > 0) {
      let mod = (temp - 1) % 26;
      letter = String.fromCharCode(65 + mod) + letter;
      temp = Math.floor((temp - mod) / 26);
    }

    return letter;
  }

  // Format date for filename (YYYYMMDD_HHMMSS)
  public formatDateForFileName(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
  }

  // Helper function to set cell style
  public setCellStyle(stylesObj, row, col, styleProps) {
    const cellKey = `${row},${col}`;
    stylesObj[cellKey] = styleProps;
  }

  // Helper function to check if column is excluded
  public isExcludedColumn(colIndex, excludedIndices) {
    return excludedIndices.includes(colIndex);
  }

  // Add this helper function at the bottom of the file, before Office.onReady
  public isDateValue(value) {
    // check if value matches this regex \b(3[5-9][0-9]{3}|4[0-9]{4})\b
    return String(value).match(/\b(3[5-9][0-9]{3}|4[0-9]{4})\b/) !== null;
  }

  public formatExcelDate(excelDate, use1904System = false) {
    // Convert to number if it's not already
    const serialNumber = Number(excelDate);

    // Check if it's a valid number
    if (isNaN(serialNumber)) {
      return "Invalid date value";
    }

    // Calculate the date based on the Excel system
    // Excel for Windows uses 1900 date system, with day 1 = January 1, 1900
    // but Excel incorrectly treats 1900 as a leap year, so we need to adjust

    // For the 1904 date system (older Mac Excel), day 0 = January 1, 1904
    let dateObj;
    if (use1904System) {
      // 1904 date system
      dateObj = new Date(serialNumber * 86400000 + new Date(1904, 0, 1).getTime());
    } else {
      // 1900 date system
      // Excel has a bug treating 1900 as a leap year, so dates before March 1, 1900 need adjustment
      // Serial number 60 corresponds to February 29, 1900, which didn't exist
      let adjustedSerial = serialNumber;
      if (serialNumber < 60) {
        // Before the phantom Feb 29, all dates are 1 day earlier
        dateObj = new Date(adjustedSerial * 86400000 + new Date(1900, 0, 0).getTime());
      } else if (serialNumber === 60) {
        // The phantom Feb 29 - we'll show it as Feb 29 to match Excel
        dateObj = new Date(1900, 1, 29);
      } else {
        // After the phantom date, we need to subtract 1 from the serial
        adjustedSerial = serialNumber - 1;
        dateObj = new Date(adjustedSerial * 86400000 + new Date(1900, 0, 0).getTime());
      }
    }

    // Format the date components
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    // Get hours for AM/PM format
    let hours = dateObj.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM

    const hoursStr = String(hours).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getSeconds()).padStart(2, "0");

    // Return formatted date string
    let resultDate = `${day}/${month}/${year}`;

    let resultTime = `${hoursStr}:${minutes}:${seconds} ${ampm}`;

    // if the excel date has decimal part, then it is a timestamp, combine the date and time
    if (serialNumber % 1 !== 0) {
      return `${resultDate} ${resultTime}`;
    }

    return resultDate;
  }

  public async readExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // Show progress for large files
      const isLargeFile = file.size > 5 * 1024 * 1024; // 5MB
      const isVeryLargeFile = file.size > 20 * 1024 * 1024; // 20MB

      if (isLargeFile) {
        this.showSuccess({
          message: `Reading large file (${(file.size / (1024 * 1024)).toFixed(1)}MB)...`,
          cleanUp: false,
        });
      }

      // For very large files, use optimized reading
      if (isVeryLargeFile) {
        this.readLargeExcelFile(file).then(resolve).catch(reject);
        return;
      }

      reader.onload = function (e: ProgressEvent<FileReader>) {
        try {
          const data = new Uint8Array(e.target.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // Convert to a more usable format with sheet data
          const workbookData = {};
          const sheetNames = workbook.SheetNames;

          // Process sheets with progress for large files
          sheetNames.forEach((sheetName, index) => {
            if (isLargeFile) {
              const progress = Math.round(((index + 1) / sheetNames.length) * 100);
              this.showSuccess({
                message: `Processing sheet ${index + 1}/${sheetNames.length} (${progress}%)...`,
                cleanUp: false,
              });
            }

            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", raw: false });
            workbookData[sheetName] = {
              data: jsonData,
              worksheet: worksheet // Store the original worksheet for format information
            };
          });

          if (isLargeFile) {
            this.showSuccess({
              message: "File processing complete!",
              cleanUp: true,
            });
          }

          resolve(workbookData);
        } catch (error) {
          reject(error);
        }
      }.bind(this);

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // Optimized method for very large files
  private async readLargeExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const fileSize = file.size;
      const chunkSize = 10 * 1024 * 1024; // 10MB chunks
      let offset = 0;
      let chunks = [];

      this.showSuccess({
        message: `Reading very large file (${(fileSize / (1024 * 1024)).toFixed(1)}MB) in chunks...`,
        cleanUp: false,
      });

      const readNextChunk = () => {
        const slice = file.slice(offset, offset + chunkSize);
        reader.readAsArrayBuffer(slice);
      };

      reader.onload = async function (e: ProgressEvent<FileReader>) {
        try {
          chunks.push(new Uint8Array(e.target.result as ArrayBuffer));
          offset += chunkSize;

          // Show progress
          const progress = Math.round((offset / fileSize) * 100);
          this.showSuccess({
            message: `Reading file: ${progress}% complete...`,
            cleanUp: false,
          });

          if (offset < fileSize) {
            // Read next chunk
            readNextChunk();
          } else {
            // Combine all chunks
            this.showSuccess({
              message: "Combining file chunks...",
              cleanUp: false,
            });

            const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
            const combinedArray = new Uint8Array(totalLength);
            let position = 0;

            chunks.forEach((chunk) => {
              combinedArray.set(chunk, position);
              position += chunk.length;
            });

            // Clear chunks to free memory
            chunks = null;

            // Process the combined file
            this.showSuccess({
              message: "Processing Excel file...",
              cleanUp: false,
            });

            const workbook = XLSX.read(combinedArray, { type: "array" });
            const workbookData = {};
            const sheetNames = workbook.SheetNames;

            // Process sheets with progress - async for memory optimization
            const processSheets = async () => {
              for (let index = 0; index < sheetNames.length; index++) {
                const sheetName = sheetNames[index];
                const progress = Math.round(((index + 1) / sheetNames.length) * 100);

                this.showSuccess({
                  message: `Processing sheet ${index + 1}/${sheetNames.length} (${progress}%)...`,
                  cleanUp: false,
                });

                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", raw: false });
                workbookData[sheetName] = {
                  data: jsonData,
                  worksheet: worksheet // Store the original worksheet for format information
                };

                // Add small delay for very large files to prevent browser freezing
                if (fileSize > 50 * 1024 * 1024) {
                  // 50MB+
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }
              }
            };

            await processSheets();

            this.showSuccess({
              message: "Large file processing complete!",
              cleanUp: true,
            });

            resolve(workbookData);
          }
        } catch (error) {
          reject(error);
        }
      }.bind(this);

      reader.onerror = reject;
      readNextChunk();
    });
  }

  // Add a function to handle the file upload with progress
  public async handleFileUpload(event) {
    const fileInput = event.target;
    const file = fileInput.files[0];

    if (!file) {
      this.showError({ message: "No file selected" });
      return;
    }

    // Show initial progress for large files
    const isLargeFile = file.size > 5 * 1024 * 1024; // 5MB
    const isVeryLargeFile = file.size > 20 * 1024 * 1024; // 20MB

    if (isLargeFile) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const message = isVeryLargeFile
        ? `Starting upload of very large file (${fileSizeMB}MB) - this may take a while...`
        : `Starting upload of large file (${fileSizeMB}MB)...`;

      this.showSuccess({
        message: message,
        cleanUp: false,
      });
    }

    try {
      const data = await this.readExcelFile(file);
      const $sheetSelect = $(`#${fileInput.id}Select`);

      this.showWarning({
        message: "✅ We recommend using a new workbook.<br>⚠️ <strong>Sheet1</strong> will be deleted if it exists.",
        cleanUp: false,
      });

      // Store the data based on which file input was used
      if (fileInput.id === "oldFile") {
        this.state.oldFile = data;
        this.state.oldFileName = file.name;
      } else if (fileInput.id === "newFile") {
        this.state.newFile = data;
        this.state.newFileName = file.name;
      }

      $sheetSelect.empty().show();

      const SheetNames = Object.keys(data);
      if (SheetNames.length > 1) {
        $sheetSelect.append(
          $("<option>", {
            value: "",
            text: "Select sheet to compare",
          })
        );
      }

      SheetNames.forEach((sheet) => {
        $sheetSelect.append(
          $("<option>", {
            value: sheet,
            text: sheet,
          })
        );
      });

      // Show completion message with file info
      if (isLargeFile) {
        const totalRows = Object.values(data).reduce((acc, sheet) => {
          // Handle both old format (array) and new format (object with data property)
          const sheetData = Array.isArray(sheet) ? sheet : (sheet.data || []);
          return acc + sheetData.length;
        }, 0);

        this.showSuccess({
          message: `File "${file.name}" uploaded successfully! (${totalRows.toLocaleString()} total rows across ${SheetNames.length} sheets)`,
          cleanUp: true,
        });
      }
    } catch (error) {
      this.showError({
        message: `Error reading file: ${error.message}`,
        cleanUp: false,
      });
    }
  }

  public promptUser({
    suggestedValues = [],
    possibleValues = [],
    requiredInput = false,
    type = "KeyColumns",
  }: PromptUserOptions) {
    return new Promise((resolve) => {
      const $inputDialog = $("#inputDialog");
      const $promptMessage = $("#promptMessage");
      const $dialogSubtitle = $("#dialogSubtitle");
      const $dialogTitle = $("#dialogTitle");
      const $confirmButton = $("#confirmInput");
      const $cancelButton = $("#cancelInput");

      this.createMultiSelectDropdown(possibleValues, suggestedValues);
      $inputDialog.show();

      if (type === "KeyColumns") {
        $promptMessage.text(`Confirm key column names for sheet '${this.state.newSheet}':`);
        $dialogTitle.text("Key Columns (required)");
        $dialogSubtitle.text("Key columns are used to identify records during the comparison.");
      } else if (type === "ExcludedColumns") {
        $promptMessage.text(`Confirm column names to skip in the comparison:`);
        $dialogTitle.text("Skipped Columns (optional)");
        $dialogSubtitle.text(
          "Skipped columns are not used in the comparison, but will be included in the output."
        );
      }

      const handleConfirm = () => {
        const value = this.getSelectedOptions();

        if (requiredInput && !value) {
          this.showWarning({
            message: "Please select at least one value",
            id: "errorMessage",
            cleanUp: false,
          });
          return; // Keep the modal open
        }

        $("#multiSelectDropdown").empty();
        $inputDialog.hide();
        cleanup();
        resolve(value);
      };

      function handleCancel() {
        $inputDialog.hide();
        cleanup();
        resolve(null);
      }

      function handleKeyDown(e) {
        if (e.key === "Enter") {
          handleConfirm();
        } else if (e.key === "Escape") {
          handleCancel();
        }
      }

      function cleanup() {
        $confirmButton.off("click", handleConfirm);
        $cancelButton.off("click", handleCancel);
      }

      $confirmButton.on("click", handleConfirm);
      $cancelButton.on("click", handleCancel);
      $inputDialog.on("keydown", handleKeyDown);
    });
  }

  public showError({ message, id = "results", cleanUp = false }) {
    $(`#${id}`).empty();
    $(`#${id}`).html(`<p class="error">${message}</p>`);
    this.clearMessage(id, cleanUp);
  }

  public showSuccess({ message, id = "results", cleanUp = false }) {
    $(`#${id}`).empty();
    $(`#${id}`).html(`<p class="success">${message}</p>`);
    this.clearMessage(id, cleanUp);
  }

  public showWarning({ message, id = "results", cleanUp = false }) {
    $(`#${id}`).empty();
    $(`#${id}`).html(`<p class="warning">${message}</p>`);
    this.clearMessage(id, cleanUp);
  }

  private clearMessage(id = "results", cleanUp = true) {
    // clear the message after 3 seconds
    if (cleanUp == false) {
      return;
    }
    setTimeout(() => {
      $(`#${id}`).empty();
    }, 3000);
  }

  public showLoading() {
    $("#loading").show();
  }

  public hideLoading() {
    $("#loading").hide();
  }

  // Convert column name to Excel column letter
  public columnNameToLetter(headers: string[], columnName: string): string {
    const index = headers.findIndex(
      (header) => String(header).toLowerCase() === String(columnName).toLowerCase()
    );
    if (index === -1) {
      throw new Error(`Column "${columnName}" not found in headers`);
    }
    return this.indexToColLetter(index);
  }

  private renderSelectedItems(selectedOptions: string[]) {
    const $selectedItems = $("#selectedItems");
    $selectedItems.empty();

    selectedOptions.forEach((option) => {
      const $pill = $("<span>").addClass("selected-item-pill");
      const $removeBtn = $("<button>")
        .addClass("remove-pill")
        .attr("type", "button")
        .attr("data-value", option)
        .html("&times;");

      // eslint-disable-next-line no-undef
      $pill.append($removeBtn).append(document.createTextNode(option));
      $selectedItems.append($pill);
    });

    // Remove handler (delegated for dynamic content)
    $selectedItems.off("click", ".remove-pill").on("click", ".remove-pill", (e) => {
      const valueToRemove = $(e.currentTarget).attr("data-value");
      // Uncheck the corresponding checkbox
      $(`#multiSelectDropdown input[type='checkbox'][value='${valueToRemove}']`)
        .prop("checked", false)
        .trigger("change");
      // Optionally, update the pills immediately
      this.renderSelectedItems(
        $("#multiSelectDropdown input[type='checkbox']:checked")
          .map(function () {
            return $(this).val();
          })
          .get()
      );
    });
  }

  private createMultiSelectDropdown(options: string[], selectedOptions: string[]) {
    const $dropdownContainer = $("#multiSelectDropdownContainer");
    const $dropdown = $("#multiSelectDropdown");

    $dropdown.empty();

    options.forEach((option) => {
      const $div = $("<div>").addClass("multiSelectDropdownItem");
      const $checkbox = $("<input>", {
        type: "checkbox",
        value: option,
        checked: selectedOptions.includes(option),
      });
      const $label = $("<label>").text(option);

      $div.append($checkbox, $label);
      $dropdown.append($div);
    });

    // Render selected items as pills
    this.renderSelectedItems(selectedOptions);

    // Listen for changes to update pills
    $("#multiSelectDropdown input[type='checkbox']").on("change", () => {
      const selected = $("#multiSelectDropdown input[type='checkbox']:checked")
        .map(function () {
          return $(this).val();
        })
        .get();
      this.renderSelectedItems(selected);
    });

    $dropdownContainer.append($dropdown);
  }

  public getSelectedOptions() {
    const selectedOptions = $("#multiSelectDropdown input[type='checkbox']:checked")
      .map(function () {
        return $(this).val();
      })
      .get();

    return selectedOptions.join(",");
  }
}
